import { IBFActivate, IBF_ACTIVATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_ACTIVATE";
import { IBFAlarm, IBF_ALARM } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_ALARM";
import { IBFBasalRate, IBF_BASAL_RATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BASAL_RATE";
import { IBF_BLOOD_GLUCOSE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BLOOD_GLUCOSE";
import { IBFBolus, IBF_BOLUS } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BOLUS";
import { IBF_CARBS } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_CARBS";
import { IBF_DATE_CHANGE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_DATE_CHANGE";
import { IBFDeactivate, IBF_DEACTIVATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_DEACTIVATE";
import { IBF_DOWNLOAD } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_DOWNLOAD";
import { IBF_END_MARKER } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_END_MARKER";
import { IBF_OCCLUSION } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_OCCLUSION";
import { IBF_REMOTE_HAZARD_ALARM } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_REMOTE_HAZARD_ALARM";
import { IBFResume, IBF_RESUME } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_RESUME";
import {
  IBFSuggestedCalculation,
  IBF_SUGGESTED_CALCULATION,
} from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_SUGGESTED_CALCULATION";
import { IBFSuspend, IBF_SUSPEND } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_SUSPEND";
import { IBF_TERMINATE_BASAL } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_TERMINATE_BASAL";
import { IBF_TERMINATE_BOLUS } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_TERMINATE_BOLUS";
import { IBF_TIME_CHANGE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_TIME_CHANGE";
import { IBFRecord } from "@/lib/drivers/insulet/IBFRecords/IBFRecord";
import { IBFRecordParsingInfo } from "@/lib/drivers/insulet/IBFRecords/RecordParser";

export type IBFHistoryRecordDefinition<T> = {
  value: number;
  name: string;
  description?: string;
  size: number;
  parse: (record: IBFRecord, info: IBFRecordParsingInfo) => T;
};

type HistoryRecordErrorType =
  | "RECORD_SIZE_MISMATCH"
  | "RECORD_VALUE_MISMATCH"
  | "HISTORY_RECORD_VALUE_MISMATCH"
  | "EXPECTED_RECORD_MISSING";

export class HistoryRecordError extends Error {
  constructor(public type: HistoryRecordErrorType) {
    super(type);
  }
}

export const ALL_HISTORY_RECORDS = {
  [IBF_ACTIVATE.value]: IBF_ACTIVATE,
  [IBF_ALARM.value]: IBF_ALARM,
  [IBF_BASAL_RATE.value]: IBF_BASAL_RATE,
  [IBF_BLOOD_GLUCOSE.value]: IBF_BLOOD_GLUCOSE,
  [IBF_BOLUS.value]: IBF_BOLUS,
  [IBF_CARBS.value]: IBF_CARBS,
  [IBF_DATE_CHANGE.value]: IBF_DATE_CHANGE,
  [IBF_DEACTIVATE.value]: IBF_DEACTIVATE,
  [IBF_DOWNLOAD.value]: IBF_DOWNLOAD,
  [IBF_END_MARKER.value]: IBF_END_MARKER,
  [IBF_OCCLUSION.value]: IBF_OCCLUSION,
  [IBF_REMOTE_HAZARD_ALARM.value]: IBF_REMOTE_HAZARD_ALARM,
  [IBF_RESUME.value]: IBF_RESUME,
  [IBF_SUGGESTED_CALCULATION.value]: IBF_SUGGESTED_CALCULATION,
  [IBF_SUSPEND.value]: IBF_SUSPEND,
  [IBF_TERMINATE_BASAL.value]: IBF_TERMINATE_BASAL,
  [IBF_TERMINATE_BOLUS.value]: IBF_TERMINATE_BOLUS,
  [IBF_TIME_CHANGE.value]: IBF_TIME_CHANGE,
};

const HISTORY_RECORD_TYPES = {
  [IBF_ACTIVATE.value]: IBF_ACTIVATE,
  [IBF_DEACTIVATE.value]: IBF_DEACTIVATE,
  [IBF_RESUME.value]: IBF_RESUME,
  [IBF_SUSPEND.value]: IBF_SUSPEND,
  [IBF_ALARM.value]: IBF_ALARM,

  [IBF_BASAL_RATE.value]: IBF_BASAL_RATE,

  [IBF_BOLUS.value]: IBF_BOLUS,
  [IBF_SUGGESTED_CALCULATION.value]: IBF_SUGGESTED_CALCULATION,
};

type HistoryRecordData =
  | IBFActivate
  | IBFDeactivate
  | IBFResume
  | IBFSuspend
  | IBFBasalRate
  | IBFBolus
  | IBFSuggestedCalculation
  | IBFAlarm;

export const historyRecordParser = (
  logId: number,
  record: IBFRecord,
  info: IBFRecordParsingInfo,
): { name: string; data: HistoryRecordData } | undefined => {
  if (!(logId in HISTORY_RECORD_TYPES)) {
    // We don't want to parse that event
    return undefined;
  }
  const definition = HISTORY_RECORD_TYPES[logId];

  if (record.remaining() < definition.size) {
    throw new HistoryRecordError("RECORD_SIZE_MISMATCH");
  }

  try {
    return { name: definition.name, data: definition.parse(record, info) };
  } catch (e) {
    console.error("Error parsing history record", e);
    throw new HistoryRecordError("HISTORY_RECORD_VALUE_MISMATCH");
  }
};
